import React, { lazy } from "react";
import MainLayout from "../Layouts/MainLayout/index.js";
import FallbackLoader from "../Components/loaders/FallbackLoader/FallbackLoader";
import DynamicDashboard from "./DynamicDashboard.js";
import { PrivateRoute } from "./PrivateRoute.js";
import { AdminRoute } from "./AdminRoute.js";

import { Outlet } from "react-router-dom";
import Contact from "../Pages/Contact/Contact.jsx";
import Privacy from "../Pages/Privacy/Privacy.jsx";
import DashboardUser from "../MakersV2/Pages/Student/DashboardUser.jsx";
import DashboardAdmin from "../MakersV2/Pages/Admin/DashboardAdmin.jsx";
import Bookings from "../MakersV2/Pages/Admin/Bookings.jsx";
import MeetingRooms from "../MakersV2/Pages/Admin/MeetingRooms.jsx";
import EditMeetingRoom from "../MakersV2/Pages/Admin/EditMeetingRoom.jsx";
import EduspaceDashboard from "../MakersV2/Pages/Admin/EduspaceDashboard.jsx";
import EduspaceGuidelines from "../MakersV2/Pages/Admin/EduspaceGuidelines.jsx";
import UserManagement from "../MakersV2/Pages/Admin/UserManagementDashboard.jsx";

const MyBookings = lazy(() => import("../Pages/MyBookings/MyBookings.jsx"));

const AvailableRooms = lazy(() =>
	import("../Pages/AvailableRooms/AvailableRooms.jsx")
);

const GetAllBookings = lazy(() =>
	import("../Pages/GetAllBookings/GetAllBookings.jsx")
);

const AllRooms = lazy(() => import("../Pages/AllRooms/AllRooms.jsx"));

const UserDashboard = lazy(() =>
	import("../Pages/UserManagementDashboard/UserManagementDashboard.jsx")
);

const ClassroomAvailableRooms = lazy(() =>
	import("../Pages/ClassroomAvailableRooms/ClassroomAvailableRooms.jsx")
);

const ClassroomMyRequests = lazy(() =>
	import("../Pages/ClassroomMyRequests/ClassroomMyRequests.jsx")
);

const ClassroomAllRequests = lazy(() =>
	import("../Pages/ClassroomAllRequests/ClassroomAllRequests.jsx")
);

const MainRoutes = [
	{
		path: "/",
		element: <PrivateRoute />,
		children: [
			{
				element: <MainLayout />,
				children: [
					// ---- User-accessible routes (any authenticated role) ----
					{
						index: true,
						element: (
							<React.Suspense fallback={<FallbackLoader />}>
								<DashboardUser />
							</React.Suspense>
						),
					},
					{
						path: "user-dashboard",
						element: (
							<React.Suspense fallback={<FallbackLoader />}>
								<DashboardUser />
							</React.Suspense>
						),
					},
					{
						path: "/",
						element: (
							<React.Suspense fallback={<FallbackLoader />}>
								<UserDashboard />
							</React.Suspense>
						),
					},
					{
						path: "classroom-available-rooms",
						element: (
							<React.Suspense fallback={<FallbackLoader />}>
								<ClassroomAvailableRooms />
							</React.Suspense>
						),
					},
					{
						path: "classroom-my-requests",
						element: (
							<React.Suspense fallback={<FallbackLoader />}>
								<ClassroomMyRequests />
							</React.Suspense>
						),
					},
					// Deliberately NOT admin-guarded: "Cowork Class User" links here from
					// the side menu too. The page hides its admin-only surfaces (stats,
					// other people's pending requests, colour management, "View Requests")
					// from anyone who is not a Cowork Class Admin.
					{
						path: "eduspace-dashboard",
						element: (
							<React.Suspense fallback={<FallbackLoader />}>
								<EduspaceDashboard />
							</React.Suspense>
						),
					},
					// Static help page: booking guidelines + tutorial videos. Open to both
					// "Cowork Class Admin" and "Cowork Class User".
					{
						path: "eduspace-guidelines",
						element: (
							<React.Suspense fallback={<FallbackLoader />}>
								<EduspaceGuidelines />
							</React.Suspense>
						),
					},
					// ---- Admin-only routes (guarded by AdminRoute) ----
					{
						element: <AdminRoute />,
						children: [
							{
								path: "MeetingRooms",
								element: (
									<React.Suspense fallback={<FallbackLoader />}>
										<MeetingRooms />
									</React.Suspense>
								),
							},
							{
								path: "EditMeetingRooms",
								element: (
									<React.Suspense fallback={<FallbackLoader />}>
										<EditMeetingRoom />
									</React.Suspense>
								),
							},
							{
								path: "user-bookings",
								element: (
									<React.Suspense fallback={<FallbackLoader />}>
										<Bookings />
									</React.Suspense>
								),
							},
							{
								path: "admin-dashboard",
								element: (
									<React.Suspense fallback={<FallbackLoader />}>
										<DashboardAdmin />
									</React.Suspense>
								),
							},
							{
								path: "bookings",
								element: (
									<React.Suspense fallback={<FallbackLoader />}>
										<Bookings />
									</React.Suspense>
								),
							},
							{
								path: "meeting-room",
								element: (
									<React.Suspense fallback={<FallbackLoader />}>
										<MeetingRooms />
									</React.Suspense>
								),
							},
							{
								path: "edit-meeting-room",
								element: (
									<React.Suspense fallback={<FallbackLoader />}>
										<EditMeetingRoom />
									</React.Suspense>
								),
							},
							{
								path: "/usermanagement",
								element: (
									<React.Suspense fallback={<FallbackLoader />}>
										<UserDashboard />
									</React.Suspense>
								),
							},
							{
								path: "user-management",
								element: (
									<React.Suspense fallback={<FallbackLoader />}>
										<UserManagement />
									</React.Suspense>
								),
							},
							{
								path: "allroombookings",
								element: (
									<React.Suspense fallback={<FallbackLoader />}>
										<GetAllBookings />
									</React.Suspense>
								),
							},
							{
								path: "allrooms",
								element: (
									<React.Suspense fallback={<FallbackLoader />}>
										<AllRooms />
									</React.Suspense>
								),
							},
							{
								path: "allLabs",
								element: (
									<React.Suspense fallback={<FallbackLoader />}>
										<AllRooms isLab={true} />
									</React.Suspense>
								),
							},
							{
								path: "classroom-all-requests",
								element: (
									<React.Suspense fallback={<FallbackLoader />}>
										<ClassroomAllRequests />
									</React.Suspense>
								),
							},
						],
					},
				],
			},
		],
	},
	{
		path: "/contact",
		element: <Contact />,
	},
	{
		path: "/privacy",
		element: <Privacy />,
	},
];

export default MainRoutes;
